// src/pages/BO/Schools.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { schoolService, School } from '../../services/schoolService';
import { useAuth } from '../../context/AuthContext';
import TableActions from '../../components/TableActions';

const Schools: React.FC = () => {
    const { t } = useTranslation();
    const { userType, setSchoolId } = useAuth();
    const navigate = useNavigate();
    const [schools, setSchools] = useState<School[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const list = await schoolService.getSchools();
                setSchools(list);
            } catch (e) {
                setError(t('school.loadFailed'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [t]);

    const handleAccess = (school: School) => {
        setSchoolId(school.id, school);
        navigate('/courses');
    };

    const handleEdit = (school: School) => {
        navigate(`/schools/${school.id}/edit`);
    };

    const handleCreate = () => {
        navigate('/schools/create');
    };

    if (loading) return (
        <div className="bo-container">
            <div className="loading">{t('common.loading')}</div>
        </div>
    );

    if (error) return (
        <div className="bo-container">
            <div className="error-message" style={{ textAlign: 'center' }}>
                {error}
            </div>
        </div>
    );

    return (
        <div className="bo-container">
            <div className="bo-header">
                <h2>{t('school.school')}</h2>
                {userType === 'SUPER_ADMIN' && (
                    <button
                        type="button"
                        className="btn-create"
                        onClick={handleCreate}
                    >
                        + {t('school.createSchool')}
                    </button>
                )}
            </div>

        <div className="bo-table-container">
            <div className="bo-table-wrapper">
                <table className="bo-table">
                    <thead>
                        <tr>
                            <th>{t('school.name')}</th>
                            <th>{t('school.address')}</th>
                            <th>{t('school.phone')}</th>
                            <th>{t('school.email')}</th>
                            <th>{t('school.enabled')}</th>
                            {userType === 'SUPER_ADMIN' && <th>{t('common.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {schools.map(s => (
                            <tr key={s.id}>
                                <td style={{ fontWeight: 500 }}>{s.name}</td>
                                <td>{s.address || '-'}</td>
                                <td>{s.phone || '-'}</td>
                                <td>{s.email || '-'}</td>
                                <td>
                                    <span className={`status-badge ${s.enabled ? 'enabled' : 'disabled'}`}>
                                        {s.enabled ? t('common.enabled') : t('common.disabled')}
                                    </span>
                                </td>
                                {userType === 'SUPER_ADMIN' && (
                                    <td>
                                        <TableActions actions={[
                                            {
                                                label: t('common.edit'),
                                                onClick: () => handleEdit(s),
                                                className: 'btn-edit',
                                                color: '#4338ca'
                                            },
                                            {
                                                label: t('school.access'),
                                                onClick: () => handleAccess(s),
                                                className: 'btn-view',
                                                color: '#c2410c'
                                            }
                                        ]} />
                                    </td>
                                )}
                            </tr>
                        ))}
                        {schools.length === 0 && (
                            <tr>
                                <td colSpan={6} className="empty-state">
                                    {t('school.noSchoolsAvailable')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);
};

export default Schools;
